/**
 * Keyboard Shortcuts Cheatsheet
 * Floating reference card that users can pin open
 */
import React, { useState, useEffect } from 'react';
import { Keyboard, X, Pin, PinOff, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatShortcut, SHORTCUTS } from '../hooks/useKeyboardShortcuts';

// Default shortcuts to display
const DEFAULT_SHORTCUTS = {
  GENERATE: { key: 'g', ctrl: true, shift: false, description: 'Generate content' },
  SCHEDULE: { key: 's', ctrl: true, shift: false, description: 'Open schedule modal' },
  ESCAPE: { key: 'Escape', ctrl: false, shift: false, description: 'Close modal' },
  HELP: { key: '/', ctrl: true, shift: false, description: 'Show/hide shortcuts' },
  SAVE: { key: 's', ctrl: true, shift: true, description: 'Save as template' },
  COPY: { key: 'c', ctrl: true, shift: true, description: 'Copy content' },
  NEW: { key: 'n', ctrl: true, shift: false, description: 'New content' },
};

export const ShortcutsCheatsheet = ({ 
  isOpen: controlledOpen, 
  onClose,
  customShortcuts = null,
  position = 'bottom-right' // bottom-right, bottom-left, top-right, top-left
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Use controlled state if provided
  const open = controlledOpen !== undefined ? controlledOpen : isOpen;
  const setOpen = controlledOpen !== undefined ? onClose : setIsOpen;

  // Listen for Ctrl+/ to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === '/') {
        e.preventDefault();
        if (controlledOpen !== undefined) {
          onClose?.();
        } else {
          setIsOpen(prev => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [controlledOpen, onClose]);

  // Load pinned state from localStorage
  useEffect(() => {
    const pinned = localStorage.getItem('shortcuts-pinned');
    if (pinned === 'true') {
      setIsPinned(true);
      setIsOpen(true);
    }
  }, []);

  // Save pinned state
  useEffect(() => {
    localStorage.setItem('shortcuts-pinned', isPinned ? 'true' : 'false');
  }, [isPinned]);

  const handleClose = () => {
    if (!isPinned) {
      if (controlledOpen !== undefined) {
        onClose?.();
      } else {
        setIsOpen(false);
      }
    }
  };

  const togglePin = () => {
    setIsPinned(prev => !prev);
  };

  const toggleMinimize = () => {
    setIsMinimized(prev => !prev);
  };

  // Drag handlers
  const handleDragStart = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleDrag = (e) => {
    if (!isDragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragStart]);

  // Get shortcuts to display
  const shortcuts = customShortcuts || (typeof SHORTCUTS !== 'undefined' ? SHORTCUTS : DEFAULT_SHORTCUTS);

  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4',
  };

  if (!open) {
    // Show floating trigger button
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen ? setOpen(false) : setIsOpen(true)}
        className={`fixed ${positionClasses[position]} z-40 shadow-lg bg-white dark:bg-slate-800`}
        aria-label="Show keyboard shortcuts (Ctrl+/)"
        data-testid="shortcuts-trigger"
      >
        <Keyboard className="w-4 h-4 mr-2" />
        <span className="hidden sm:inline">Shortcuts</span>
        <Badge variant="secondary" className="ml-2 font-mono text-xs">⌘/</Badge>
      </Button>
    );
  }

  return (
    <Card
      className={`fixed z-50 shadow-xl transition-all duration-200 ${
        isMinimized ? 'w-auto' : 'w-80'
      } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${
        offset.x !== 0 || offset.y !== 0 ? '' : positionClasses[position]
      }`}
      style={offset.x !== 0 || offset.y !== 0 ? {
        bottom: `calc(1rem - ${offset.y}px)`,
        right: `calc(1rem - ${offset.x}px)`,
      } : {}}
      data-testid="shortcuts-cheatsheet"
      role="dialog"
      aria-label="Keyboard shortcuts reference"
    >
      <CardHeader 
        className="pb-2 cursor-grab active:cursor-grabbing"
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Keyboard className="w-4 h-4 text-purple-500" />
            {!isMinimized && 'Keyboard Shortcuts'}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={toggleMinimize}
              aria-label={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? (
                <Maximize2 className="w-3 h-3" />
              ) : (
                <Minimize2 className="w-3 h-3" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 ${isPinned ? 'text-purple-600' : ''}`}
              onClick={togglePin}
              aria-label={isPinned ? 'Unpin' : 'Pin open'}
              aria-pressed={isPinned}
            >
              {isPinned ? (
                <Pin className="w-3 h-3 fill-current" />
              ) : (
                <PinOff className="w-3 h-3" />
              )}
            </Button>
            {!isPinned && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleClose}
                aria-label="Close shortcuts"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      {!isMinimized && (
        <CardContent className="pt-0">
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {Object.entries(shortcuts).map(([key, shortcut]) => (
              <div 
                key={key}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {shortcut.description}
                </span>
                <Badge 
                  variant="outline" 
                  className="font-mono text-xs bg-gray-50 dark:bg-slate-800"
                >
                  {formatShortcut(shortcut)}
                </Badge>
              </div>
            ))}
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Press <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-xs">Ctrl+/</kbd> to toggle
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Hook to use shortcuts cheatsheet
 */
export const useShortcutsCheatsheet = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggle = () => setIsOpen(prev => !prev);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  
  return { isOpen, toggle, open, close, setIsOpen };
};

export default ShortcutsCheatsheet;
